import { hc } from "hono/client";

import { auth } from "~/lib/auth";
import { env } from "~/lib/env";
import type { AppType } from "../../hono-api";

const GENERIC_API_ERROR = "Er ging iets mis. Probeer het opnieuw.";
const NETWORK_ERROR =
  "We konden geen verbinding maken. Controleer je internet en probeer het opnieuw.";

function mapRawErrorToDutch(rawMessage?: string): string {
  if (!rawMessage) {
    return GENERIC_API_ERROR;
  }

  const normalizedMessage = rawMessage.toLowerCase().trim();

  if (
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("networkerror") ||
    normalizedMessage.includes("load failed")
  ) {
    return NETWORK_ERROR;
  }

  if (normalizedMessage.includes("timeout")) {
    return "De verbinding duurde te lang. Probeer het opnieuw.";
  }

  if (
    normalizedMessage.includes("unauthorized") ||
    normalizedMessage.includes("forbidden") ||
    normalizedMessage.includes("not allowed")
  ) {
    return "Je hebt geen toegang tot deze actie.";
  }

  if (normalizedMessage.includes("not found")) {
    return "We konden de gevraagde gegevens niet vinden.";
  }

  if (
    normalizedMessage.includes("bad request") ||
    normalizedMessage.includes("internal server error") ||
    normalizedMessage.includes("invalid")
  ) {
    return GENERIC_API_ERROR;
  }

  return rawMessage;
}

function getErrorMessageFromPayload(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  if ("error" in payload && typeof payload.error === "string") {
    return payload.error;
  }

  if ("message" in payload && typeof payload.message === "string") {
    return payload.message;
  }

  return undefined;
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return mapRawErrorToDutch(error.message);
  }
  return GENERIC_API_ERROR;
}

async function readErrorMessage(response: Response): Promise<string> {
  if (response.status === 400) {
    return "Controleer de ingevulde gegevens.";
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const payload: unknown = await response.json().catch(() => null);
    return mapRawErrorToDutch(getErrorMessageFromPayload(payload) ?? response.statusText);
  }
  const text = contentType.includes("text/plain")
    ? await response.text().catch(() => "")
    : "";
  if (text.trim()) return mapRawErrorToDutch(text.trim());
  return response.status >= 500
    ? GENERIC_API_ERROR
    : mapRawErrorToDutch(response.statusText);
}

export const client = hc<AppType>(env.apiUrl, {
  headers: async (): Promise<Record<string, string>> => {
    const token = await auth.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    let response: Response;
    try {
      response = await fetch(input, init);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiError(NETWORK_ERROR, 0);
    }

    if (!response.ok) {
      throw new ApiError(await readErrorMessage(response), response.status);
    }

    return response;
  },
});
